import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface MealCaptureModalProps {
  meal: {
    id: string;
    meal_name: string;
    scheduled_time: string | null;
    day_number: number | null;
  };
  token: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function MealCaptureModal({ meal, token, onClose, onSuccess }: MealCaptureModalProps) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraActive, setCameraActive] = useState(false);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
      }
    } catch (error) {
      console.error("Error starting camera:", error);
      toast.error("Erro ao acessar câmera");
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0);
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `meal-${Date.now()}.jpg`, { type: "image/jpeg" });
          setFile(file);
          setPreview(URL.createObjectURL(file));
          stopCamera();
        }
      }, "image/jpeg", 0.9);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      stopCamera();
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Selecione ou capture uma foto primeiro");
      return;
    }

    setLoading(true);

    try {
      // Upload to storage
      const fileName = `${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("assisted-submissions")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Submit to edge function
      const { error: submitError } = await supabase.functions.invoke(
        "submit-meal-photo",
        {
          body: {
            token,
            mealItemId: meal.id,
            photoPath: fileName,
            dayNumber: meal.day_number,
            scheduledTime: meal.scheduled_time
          }
        }
      );

      if (submitError) throw submitError;

      toast.success("Foto enviada com sucesso! IA está analisando...");
      onSuccess();
    } catch (error) {
      console.error("Error submitting photo:", error);
      toast.error("Erro ao enviar foto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>📷 Registrar Refeição</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {meal.meal_name} • {meal.scheduled_time || "Horário não especificado"}
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Preview/Camera area */}
          <div className="relative w-full h-[400px] bg-muted rounded-lg overflow-hidden flex items-center justify-center">
            {preview ? (
              <img 
                src={preview} 
                alt="Preview" 
                className="max-w-full max-h-full object-contain"
              />
            ) : cameraActive ? (
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center text-muted-foreground">
                <Camera className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Capture ou selecione uma foto da sua refeição</p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="space-y-3">
            {preview ? (
              <>
                <Button 
                  onClick={() => {
                    setPreview(null);
                    setFile(null);
                  }}
                  variant="outline"
                  className="w-full"
                >
                  Tirar Outra Foto
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full h-12 text-base"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "🚀 Enviar para Análise"
                  )}
                </Button>
              </>
            ) : (
              <>
                {cameraActive ? (
                  <Button 
                    onClick={capturePhoto}
                    className="w-full h-12 text-base"
                    size="lg"
                  >
                    <Camera className="h-5 w-5 mr-2" />
                    Capturar Foto
                  </Button>
                ) : (
                  <Button 
                    onClick={startCamera}
                    className="w-full h-12 text-base"
                    size="lg"
                  >
                    <Camera className="h-5 w-5 mr-2" />
                    Abrir Câmera
                  </Button>
                )}
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      ou
                    </span>
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  variant="outline"
                  className="w-full h-12 text-base"
                  size="lg"
                >
                  <Upload className="h-5 w-5 mr-2" />
                  Selecionar da Galeria
                </Button>
              </>
            )}
          </div>

          {/* Tip */}
          <div className="bg-muted p-4 rounded-lg text-sm text-muted-foreground">
            <p>
              💡 <strong>Dica:</strong> Fotografe toda a refeição de cima para baixo para uma análise mais precisa.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
